<template>
  <div class="min-h-screen flex flex-col justify-center py-12 login-background">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="glassmorphism py-10 px-6 shadow-2xl rounded-2xl login-card">
        <div class="mb-8">
          <img class="mx-auto h-20 w-auto drop-shadow-xl" :src="deepFinanceLogo" alt="Deep Finance" />
        </div>
        <!-- Formulário de Login -->
        <form class="space-y-6" @submit.prevent="handleLogin">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700">
              Email
            </label>
            <div class="mt-1">
              <input
                id="email"
                v-model="email"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm transition duration-150 ease-in-out"
                :class="{ 'opacity-50': isLoading }"
                :disabled="isLoading"
              />
            </div>
          </div>

          <div>
            <label for="password" class="block text-sm font-medium text-gray-700">
              Senha
            </label>
            <div class="mt-1 relative">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                autocomplete="current-password"
                required
                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm pr-10 transition duration-150 ease-in-out"
                :class="{ 'opacity-50': isLoading }"
                :disabled="isLoading"
              />
              <button
                type="button"
                @click="togglePassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" class="text-gray-400 hover:text-gray-500"></i>
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input
                id="remember-me"
                v-model="rememberMe"
                name="remember-me"
                type="checkbox"
                class="h-4 w-4 text-primary focus:ring-primary-light border-gray-300 rounded cursor-pointer"
              />
              <label for="remember-me" class="ml-2 block text-sm text-gray-700 cursor-pointer">
                Lembrar-me
              </label>
            </div>

            <div class="text-sm">
              <a
                href="#"
                class="font-medium text-primary hover:text-primary-light transition-colors"
                @click.prevent="showResetPasswordDialog = true"
              >
                Esqueceu a senha?
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              class="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-[#33CC33] hover:bg-[#2eb82e] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#33CC33] transition-all duration-150 ease-in-out transform hover:-translate-y-0.5 active:translate-y-0"
              :class="{ 'opacity-50 cursor-not-allowed': isLoading }"
              :disabled="isLoading"
            >
              <i v-if="isLoading" class="pi pi-spin pi-spinner mr-2"></i>
              {{ isLoading ? 'Entrando...' : 'Entrar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Diálogo de 2FA -->
  <Dialog
    v-model:visible="show2FADialog"
    modal
    :closable="false"
    :style="{ width: '90%', maxWidth: '400px' }"
    class="p-fluid"
  >
    <template #header>
      <h3 class="text-xl font-semibold text-gray-900">Verificação em Duas Etapas</h3>
    </template>
    
    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        Digite o código de 6 dígitos do seu aplicativo autenticador
      </p>
      <div>
        <input
          v-model="twoFactorCode"
          type="text"
          maxlength="6"
          placeholder="000000"
          class="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm text-center tracking-widest"
          :class="{ 'opacity-50': isLoading }"
          :disabled="isLoading"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end space-x-3">
        <Button
          label="Verificar"
          @click="handle2FAVerification"
          :loading="isLoading"
          class="p-button-primary"
        />
      </div>
    </template>
  </Dialog>

  <!-- Diálogo de Redefinição de Senha -->
  <Dialog
    v-model:visible="showResetPasswordDialog"
    modal
    :style="{ width: '90%', maxWidth: '400px' }"
    class="p-fluid"
  >
    <template #header>
      <h3 class="text-xl font-semibold text-gray-900">Redefinir Senha</h3>
    </template>

    <div class="space-y-4">
      <p class="text-sm text-gray-600">
        Digite seu email para receber as instruções de redefinição de senha
      </p>
      <div>
        <input
          v-model="resetEmail"
          type="email"
          placeholder="seu@email.com"
          class="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
          :class="{ 'opacity-50': resettingPassword }"
          :disabled="resettingPassword"
        />
      </div>
      <div v-if="resetError" class="text-sm text-red-600">
        {{ resetError }}
      </div>
      <div v-if="resetSuccess" class="text-sm text-green-600">
        {{ resetSuccess }}
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end space-x-3">
        <Button
          label="Cancelar"
          @click="showResetPasswordDialog = false"
          class="p-button-text"
          :disabled="resettingPassword"
        />
        <Button
          label="Enviar"
          @click="handleResetPassword"
          :loading="resettingPassword"
          class="p-button-primary"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import deepFinanceLogo from '@/assets/logos/logo_deep_finance.svg'
import financeBgLight from '@/assets/images/background_login.jpg'
import { useToast } from 'primevue/usetoast'
import { sendPasswordResetEmail } from 'firebase/auth'
import { auth } from '../../firebase/config'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'

const router = useRouter()
const authStore = useAuthStore()
const toast = useToast()

// Refs
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(false)
const showResetPasswordDialog = ref(false)
const resetEmail = ref('')
const resetError = ref('')
const resetSuccess = ref('')
const resettingPassword = ref(false)
const isLoading = ref(false)
const show2FADialog = ref(false)
const twoFactorCode = ref('')

// Computed
const requires2FA = computed(() => {
  const isPending = authStore.pendingVerification
  console.log('[Login] Estado do 2FA:', {
    pendingVerification: isPending,
    show2FADialog: show2FADialog.value,
    userData: authStore.userData,
    twoFactorEnabled: authStore.userData?.twoFactorEnabled,
    twoFactorSecret: !!authStore.userData?.twoFactorSecret
  })
  return isPending
})

// Watchers
watch(() => authStore.isAuthenticated, (isAuthenticated) => {
  console.log('[Login] Estado de autenticação mudou:', {
    isAuthenticated,
    pendingVerification: authStore.pendingVerification
  })
  
  if (isAuthenticated && !authStore.pendingVerification) {
    console.log('[Login] Redirecionando para o dashboard...')
    router.push('/dashboard')
  }
})

watch(() => authStore.pendingVerification, (isPending) => {
  console.log('[Login] Estado de verificação mudou:', {
    isPending,
    isAuthenticated: authStore.isAuthenticated
  })
  
  show2FADialog.value = isPending
  if (!isPending) {
    twoFactorCode.value = ''
  }
})

// Watch para debug do diálogo
watch(show2FADialog, (newValue) => {
  console.log('[Login] show2FADialog mudou:', {
    newValue,
    requires2FA: requires2FA.value,
    pendingVerification: authStore.pendingVerification,
    userData: authStore.userData,
    twoFactorEnabled: authStore.userData?.twoFactorEnabled,
    twoFactorSecret: !!authStore.userData?.twoFactorSecret
  })
})

// Carregar email salvo se existir
onMounted(() => {
  const savedEmail = localStorage.getItem('rememberedEmail')
  if (savedEmail) {
    email.value = savedEmail
    rememberMe.value = true
  }
  
  // Se já estiver autenticado, redirecionar para o dashboard
  if (authStore.isAuthenticated) {
    isLoading.value = true
    router.replace('/dashboard')
  }
})

// Função para alternar visibilidade da senha
function togglePassword() {
  showPassword.value = !showPassword.value
}

async function handleLogin() {
  if (isLoading.value) return
  
  try {
    isLoading.value = true
    console.log('[Login] Iniciando login com:', email.value)
    
    // Validar campos
    if (!email.value || !password.value) {
      throw new Error('Por favor, preencha todos os campos')
    }
    
    // Tentar fazer login
    const result = await authStore.login(email.value, password.value)
    console.log('[Login] Resultado do login:', result)

    // Se o login foi bem sucedido e requer 2FA
    if (result.success && result.requires2FA) {
      console.log('[Login] 2FA necessário')
      show2FADialog.value = true
      return
    }

    // Se o login foi bem sucedido e não requer 2FA
    if (result.success) {
      // Salvar email se "lembrar-me" estiver marcado
      if (rememberMe.value) {
        localStorage.setItem('rememberedEmail', email.value)
      } else {
        localStorage.removeItem('rememberedEmail')
      }
      
      // Redirecionar para o dashboard
      router.push('/dashboard')
    }
  } catch (error) {
    console.error('[Login] Erro no login:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: error instanceof Error ? error.message : 'Erro ao fazer login',
      life: 3000
    })
  } finally {
    isLoading.value = false
  }
}

async function handle2FAVerification() {
  if (!twoFactorCode.value) {
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: 'Digite o código de verificação',
      life: 3000
    })
    return
  }

  try {
    isLoading.value = true
    console.log('[Login] Verificando código 2FA:', twoFactorCode.value)
    
    const result = await authStore.verify2FA(twoFactorCode.value)
    console.log('[Login] Resultado da verificação 2FA:', result)

    if (result.success) {
      // Fechar o diálogo e limpar o código
      show2FADialog.value = false
      twoFactorCode.value = ''
      
      // Salvar email se "lembrar-me" estiver marcado
      if (rememberMe.value) {
        localStorage.setItem('rememberedEmail', email.value)
      } else {
        localStorage.removeItem('rememberedEmail')
      }
      
      // Redirecionar para o dashboard
      console.log('[Login] Verificação 2FA bem sucedida, redirecionando...')
      router.push('/dashboard')
    }
  } catch (error) {
    console.error('[Login] Erro na verificação 2FA:', error)
    toast.add({
      severity: 'error',
      summary: 'Erro',
      detail: error instanceof Error ? error.message : 'Código inválido',
      life: 3000
    })
  } finally {
    isLoading.value = false
  }
}

async function handleResetPassword() {
  if (!resetEmail.value) {
    resetError.value = 'Digite seu email'
    return
  }

  try {
    resettingPassword.value = true
    resetError.value = ''
    resetSuccess.value = ''
    
    await sendPasswordResetEmail(auth, resetEmail.value)
    
    resetSuccess.value = 'Email de recuperação enviado! Verifique sua caixa de entrada.'
    setTimeout(() => {
      showResetPasswordDialog.value = false
      resetEmail.value = ''
      resetSuccess.value = ''
    }, 3000)
  } catch (error: any) {
    console.error('Erro ao enviar email de recuperação:', error)
    resetError.value = error.message || 'Erro ao enviar email de recuperação'
  } finally {
    resettingPassword.value = false
  }
}
</script>

<style scoped>
.login-background {
  background: url('@/assets/images/background_login.jpg') center/cover no-repeat;
  position: relative;
  min-height: 100vh;
}

/* Card de login com efeito de vidro desfocado */
.glassmorphism {
  background: rgba(255, 255, 255, 0.65) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 
    0 10px 25px -5px rgba(0, 0, 0, 0.15),
    0 8px 10px -6px rgba(0, 0, 0, 0.1),
    0 20px 40px -10px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.5);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.login-card:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 15px 30px -5px rgba(0, 0, 0, 0.2),
    0 10px 15px -6px rgba(0, 0, 0, 0.15),
    0 25px 50px -10px rgba(0, 0, 0, 0.15) !important;
}

/* Estilização dos inputs */
input {
  transition: all 0.2s ease-in-out;
  border-color: rgba(0, 0, 0, 0.1);
  background: #FFFFFF;
}

input:focus {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
}

/* Estilização do botão */
button {
  transition: all 0.2s ease-in-out;
}

button:not(:disabled):hover {
  box-shadow: 0 10px 15px -3px rgba(51, 204, 51, 0.2), 0 4px 6px -2px rgba(51, 204, 51, 0.1);
}

button:not(:disabled):active {
  transform: translateY(0);
}

/* Estilização do checkbox */
input[type="checkbox"] {
  transition: all 0.2s ease-in-out;
}

input[type="checkbox"]:checked {
  animation: pulse 0.2s ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* Estilização dos diálogos */
:deep(.p-dialog) {
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.03);
  background: #FFFFFF;
}

:deep(.p-dialog-header) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  background: #FFFFFF;
}

:deep(.p-dialog-content) {
  padding: 1.5rem;
  background: #FFFFFF;
}

:deep(.p-dialog-footer) {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  background: #FFFFFF;
}
</style>